import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
  Phone,
  PhoneIncoming,
  Play,
  RefreshCcw,
  Wand2,
  Edit3,
  Save,
  X,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDuration(sec) {
  if (!sec && sec !== 0) return "—";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function CallHistoryPage() {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editNotes, setEditNotes] = useState("");
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);

  const fetchCalls = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/twilio-voice/calls`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setCalls(data.calls || []);
      } else {
        toast.error("Failed to load calls");
      }
    } catch {
      toast.error("Failed to load calls");
    } finally {
      setLoading(false);
    }
  };

  const [reconciling, setReconciling] = useState(false);
  const handleReconcile = async () => {
    setReconciling(true);
    try {
      const res = await fetch(`${API}/twilio-voice/calls/reconcile`, {
        method: "POST",
        credentials: "include",
      });
      const data = await res.json();
      if (res.ok) {
        const { candidates = 0, reconciled = 0, recordings_recovered = 0 } = data;
        if (candidates === 0) {
          toast.success("No stuck calls — everything is in sync.");
        } else {
          toast.success(
            `Synced ${reconciled}/${candidates} call${candidates === 1 ? "" : "s"}` +
              (recordings_recovered ? ` · recovered ${recordings_recovered} recording${recordings_recovered === 1 ? "" : "s"}` : "")
          );
        }
        fetchCalls();
      } else {
        toast.error(data.detail || "Reconciliation failed");
      }
    } catch {
      toast.error("Reconciliation failed");
    } finally {
      setReconciling(false);
    }
  };

  useEffect(() => {
    fetchCalls();
  }, []);

  const handlePlay = (callId) => {
    setPlayingId(callId);
  };

  const handleSaveNotes = async (callId) => {
    try {
      const res = await fetch(`${API}/twilio-voice/calls/${callId}/notes`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ notes: editNotes }),
      });
      if (res.ok) {
        toast.success("Notes saved");
        setEditingId(null);
        fetchCalls();
      } else {
        toast.error("Failed to save notes");
      }
    } catch {
      toast.error("Failed to save notes");
    }
  };

  const statusVariant = (s) => {
    if (s === "completed") return "default";
    if (s === "failed" || s === "busy" || s === "no-answer") return "destructive";
    return "secondary";
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-fade-in-up" data-testid="call-history-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">
            Call History
          </h1>
          <p className="text-slate-500 mt-1">
            Recorded calls across your organization. Click play to listen back.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleReconcile}
            disabled={reconciling}
            data-testid="reconcile-calls-btn"
            title="Re-query Twilio for any call stuck in 'in-progress' and recover missing recordings"
          >
            <Wand2 className="w-4 h-4 mr-2" />
            {reconciling ? "Syncing..." : "Sync Stuck Calls"}
          </Button>
          <Button variant="outline" onClick={fetchCalls} data-testid="refresh-calls-btn">
            <RefreshCcw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <PhoneIncoming className="w-5 h-5" />
            Recent Calls
          </CardTitle>
          <CardDescription>
            {calls.length} call{calls.length === 1 ? "" : "s"} logged
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : calls.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Phone className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No calls yet. Click the Call button on a lead to get started.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Rep</TableHead>
                  <TableHead>Lead</TableHead>
                  <TableHead>To</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Recording</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {calls.map((c) => (
                  <TableRow key={c.call_id} data-testid={`call-row-${c.call_id}`}>
                    <TableCell className="whitespace-nowrap text-sm">
                      {formatDate(c.created_at)}
                    </TableCell>
                    <TableCell className="text-sm">{c.user_name || "—"}</TableCell>
                    <TableCell className="text-sm font-medium">{c.lead_name || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-500">{c.to_number}</TableCell>
                    <TableCell className="text-sm">
                      {formatDuration(c.recording_duration || c.duration_seconds)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={statusVariant(c.status)} className="text-xs">
                        {c.status || "pending"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {c.has_recording ? (
                        playingId === c.call_id ? (
                          <audio
                            ref={audioRef}
                            controls
                            autoPlay
                            className="h-8"
                            src={`${API}/twilio-voice/calls/${c.call_id}/recording`}
                            data-testid={`audio-${c.call_id}`}
                          />
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handlePlay(c.call_id)}
                            data-testid={`play-${c.call_id}`}
                          >
                            <Play className="w-3 h-3 mr-1" />
                            Play
                          </Button>
                        )
                      ) : (
                        <span className="text-xs text-slate-400">Processing…</span>
                      )}
                    </TableCell>
                    <TableCell className="max-w-xs">
                      {editingId === c.call_id ? (
                        <div className="flex items-center gap-1">
                          <Textarea
                            value={editNotes}
                            onChange={(e) => setEditNotes(e.target.value)}
                            rows={2}
                            className="text-xs"
                            data-testid={`notes-input-${c.call_id}`}
                          />
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleSaveNotes(c.call_id)}
                            data-testid={`save-notes-${c.call_id}`}
                          >
                            <Save className="w-4 h-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => setEditingId(null)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-start gap-1">
                          <span className="text-xs text-slate-600 flex-1">
                            {c.notes || <span className="text-slate-400 italic">No notes</span>}
                          </span>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6"
                            onClick={() => {
                              setEditingId(c.call_id);
                              setEditNotes(c.notes || "");
                            }}
                            data-testid={`edit-notes-${c.call_id}`}
                          >
                            <Edit3 className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
